import { type FC, type PropsWithChildren, useMemo } from 'react';

import { Box } from '@mui/material';
import { getAddress } from 'ethers';
import { useParams, useSearchParams } from 'react-router';

import CampaignInfo from '@/components/CampaignInfo';
import CampaignResultsSection from '@/components/CampaignResultsSection';
import CampaignStats from '@/components/CampaignStats';
import CancelCampaignSection from '@/components/CancelCampaignSection';
import CycleInfoSection from '@/components/CycleInfoSection';
import JoinCampaignButton from '@/components/JoinCampaignButton';
import { useReserveLayoutBottomOffset } from '@/components/Layout';
import Leaderboard from '@/components/Leaderboard';
import PageWrapper from '@/components/PageWrapper';
import RewardsDistributionWidget from '@/components/RewardsDistribution/CampaignWidget';
import { MOBILE_BOTTOM_NAV_HEIGHT } from '@/constants';
import {
  useCheckCampaignJoinStatus,
  useGetLeaderboard,
} from '@/hooks/recording-oracle/campaign';
import { useIsMobile } from '@/hooks/useBreakpoints';
import { useCampaignDetails } from '@/hooks/useCampaigns';
import { useActiveAccount } from '@/providers/ActiveAccountProvider';
import { useAuthedUserData } from '@/providers/AuthedUserData';
import { useExchangesContext } from '@/providers/ExchangesProvider';
import {
  CampaignStatus,
  CampaignType,
  type Campaign,
  type EvmAddress,
} from '@/types';
import {
  formatTokenAmount,
  isCampaignDetails,
  isThresholdBasedCampaignType,
} from '@/utils';

const BottomButtonWrapper: FC<PropsWithChildren> = ({ children }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: MOBILE_BOTTOM_NAV_HEIGHT,
        bgcolor: 'background.default',
        borderTop: '2px solid',
        borderColor: 'background.paper',
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        py: 2,
        px: 3,
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      {children}
    </Box>
  );
};

const CampaignDetails: FC = () => {
  const { address } = useParams() as { address: EvmAddress };
  const [searchParams] = useSearchParams();

  const normalizedAddress = getAddress(address) as EvmAddress;

  const { joinedCampaigns } = useAuthedUserData();
  const { exchangesMap } = useExchangesContext();
  const { activeAddress } = useActiveAccount();
  const isMobile = useIsMobile();

  const { data: campaign, isFetching: isCampaignLoading } =
    useCampaignDetails(normalizedAddress);

  const { data: leaderboard } = useGetLeaderboard({
    address: campaign?.address || '',
    enabled: campaign?.status === CampaignStatus.ACTIVE,
  });

  const { data: joinStatusInfo, isLoading: isJoinStatusLoading } =
    useCheckCampaignJoinStatus(normalizedAddress);

  const parsedData = useMemo(() => {
    const encodedData = searchParams.get('data');
    if (!encodedData) return undefined;

    try {
      const decodedData = atob(encodedData);
      const parsed = JSON.parse(decodedData);

      if (!isCampaignDetails(parsed)) {
        console.error('Invalid campaign data structure', parsed);
        return undefined;
      }

      return parsed;
    } catch (error) {
      console.error('Failed to parse encoded campaign data', error);
      return undefined;
    }
  }, [searchParams]);

  const isJoined = useMemo(() => {
    return !!joinedCampaigns?.results.some(
      (joinedCampaign) => joinedCampaign.address === normalizedAddress
    );
  }, [joinedCampaigns?.results, normalizedAddress]);

  const exchangeInfo = exchangesMap.get(campaign?.exchange_name || '');

  const campaignData = campaign || parsedData;

  const formattedFundAmount = +formatTokenAmount(
    campaignData?.fund_amount || '0',
    campaignData?.fund_token_decimals || 18
  );

  const isOngoingCampaign =
    !!campaignData &&
    campaignData.status === CampaignStatus.ACTIVE &&
    campaignData.start_date < new Date().toISOString() &&
    campaignData.end_date > new Date().toISOString();

  const isCompetitiveMmCampaign =
    campaignData?.type === CampaignType.COMPETITIVE_MARKET_MAKING;

  const showJoinCampaignButton =
    isMobile && !isJoined && isOngoingCampaign && !!exchangeInfo?.enabled;

  useReserveLayoutBottomOffset(showJoinCampaignButton);

  const showLeaderboard =
    isOngoingCampaign &&
    !isThresholdBasedCampaignType(campaignData.type) &&
    leaderboard &&
    leaderboard.data.length > 0;

  const showRewardsDistributionWidget =
    showLeaderboard && isCompetitiveMmCampaign;

  const userPosition = useMemo(() => {
    const idx = leaderboard?.data.findIndex(
      (item) => item.address.toLowerCase() === activeAddress?.toLowerCase()
    );
    return idx !== undefined && idx >= 0 ? idx + 1 : undefined;
  }, [leaderboard?.data, activeAddress]);

  return (
    <PageWrapper>
      <CampaignInfo
        campaign={campaignData}
        isCampaignLoading={isCampaignLoading}
        isOngoingCampaign={isOngoingCampaign}
        isJoined={isJoined}
        joinedAt={joinStatusInfo?.joined_at}
        isJoinStatusLoading={isJoinStatusLoading}
      />
      <CampaignStats
        campaign={campaignData}
        isCampaignLoading={isCampaignLoading}
        isJoined={isJoined}
        leaderboard={leaderboard}
      />
      {showRewardsDistributionWidget && (
        <RewardsDistributionWidget
          data={campaignData.details.rewards_distribution || []}
          fundToken={campaignData.fund_token_symbol}
          fundAmount={formattedFundAmount}
          startDate={campaignData.start_date}
          endDate={campaignData.end_date}
          userPosition={userPosition}
        />
      )}
      {isOngoingCampaign && !!leaderboard && (
        <CycleInfoSection
          campaign={campaignData}
          leaderboard={leaderboard}
          isJoined={isJoined}
        />
      )}
      {showLeaderboard && (
        <Leaderboard campaign={campaignData} leaderboard={leaderboard} />
      )}
      {!!campaign && <CampaignResultsSection campaign={campaign} />}
      {!!campaignData && <CancelCampaignSection campaign={campaignData} />}
      {showJoinCampaignButton && (
        <BottomButtonWrapper>
          <JoinCampaignButton campaign={campaignData as Campaign} />
        </BottomButtonWrapper>
      )}
    </PageWrapper>
  );
};

export default CampaignDetails;
